// Firestore Database Service - EXTENDED VERSION
import {
    collection,
    doc,
    getDoc,
    getDocs,
    getCountFromServer,
    setDoc,
    updateDoc,
    deleteDoc,

    query,
    where,
    orderBy,
    limit,
    addDoc,
    increment,
    arrayUnion,

    arrayRemove,
    onSnapshot,
    writeBatch,
    serverTimestamp,
    deleteField
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, listAll, uploadString } from 'firebase/storage';
import { db, storage } from './firebase.js';

export { serverTimestamp }; // Re-export for use in components

// ==================== USER OPERATIONS ====================

export const createUserProfile = async (uid, userData, referrerId = null) => {
    try {
        const now = new Date().toISOString();
        let badges = [
            { id: 'first_spark', name: 'First Spark', icon: 'Sparkles', unlocked: false, color: '#8b5cf6' },
            { id: 'social_butterfly', name: 'Social Butterfly', icon: 'Users', unlocked: false, color: '#ec4899' },
            { id: 'streak_master', name: 'Streak Master', icon: 'Flame', unlocked: false, color: '#ef4444' }, // Keeping for compatibility, though 'on_fire' is new standard
            { id: 'challenge_champion', name: 'Challenge Champion', icon: 'Trophy', unlocked: false, color: '#f59e0b' }
        ];

        // --- EARLY ADOPTER LOGIC ---
        let grantEarlyAdopter = false;

        // 1. Check Global Switch
        try {
            const configDoc = await getDoc(doc(db, 'system', 'config'));
            if (configDoc.exists() && configDoc.data().earlyAdopterOpen) {
                grantEarlyAdopter = true;
            }
        } catch (e) {
            console.warn("Failed to check system config for Early Adopter:", e);
        }

        // 2. Check Referrer (Friend Logic)
        if (referrerId) {
            try {
                const referrerDocRef = doc(db, 'users', referrerId);
                const referrerDoc = await getDoc(referrerDocRef);

                if (referrerDoc.exists()) {
                    const rData = referrerDoc.data();

                    // A. Badge Inheritance Logic
                    if (!grantEarlyAdopter) {
                        const hasBadge = rData.badges?.some(b => b.id === 'early_adopter' && b.unlocked);
                        if (hasBadge) {
                            grantEarlyAdopter = true;
                        }
                    }

                    // B. Referral Counting & Reward Logic (The "Ref 7 Get 1 Free" Promo)
                    const currentCount = (rData.stats?.referralCount || 0) + 1;
                    const updates = {
                        'stats.referralCount': currentCount
                    };

                    // Check for Reward (7 Friends = 7 Days Free)
                    // ONE TIME ONLY check
                    if (currentCount >= 7 && !rData.hasRedeemedReferralReward) {
                        // Grant Reward!
                        console.log(`User ${referrerId} hit 7 referrals! Granting reward.`);

                        // We need to use the imported addSubscriptionDays function, but we can't call it easily 
                        // if we are just setting doc updates here. Ideally we run a transaction or just separate updates.
                        // Let's do separate updates for safety.

                        await updateDoc(referrerDocRef, {
                            'stats.referralCount': currentCount,
                            hasRedeemedReferralReward: true
                        });

                        // Grant 7 days of 'subscriber' (Supporter)
                        // Dynamic import or assume it's available? It is exported in this file. 
                        // But we are INSIDE the module. We can just call it if defined before or after?
                        // JS hoisting works for functions.
                        await addSubscriptionDays(referrerId, 7, 'subscriber');

                    } else {
                        // Just update count
                        await updateDoc(referrerDocRef, updates);
                    }
                }
            } catch (e) {
                console.warn("Failed to process referrer logic:", e);
            }
        }

        if (grantEarlyAdopter) {
            const eaBadge = BADGES.find(b => b.id === 'early_adopter');
            if (eaBadge) {
                badges.push({ ...eaBadge, unlocked: true, unlockedAt: now });
            }
        }

        await setDoc(doc(db, 'users', uid), {
            isAdmin: false,
            isSuperAdmin: false,
            isBanned: false,
            hasSeenTutorial: false, // Initialize tutorial
            referrerId: referrerId || null,
            ...userData,
            badges, // Use our constructed badges array
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error creating user profile:', error);
        throw error;
    }
};

export const getUserProfile = async (uid) => {
    try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
            return { id: userDoc.id, ...userDoc.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting user profile:', error);
        throw error;
    }
};

export const acknowledgeGiftNotification = async (userId) => {
    try {
        await updateDoc(doc(db, 'users', userId), {
            giftNotificationPending: false
        });
    } catch (error) {
        console.error('Error acknowledging gift:', error);
        throw error;
    }
};

export const checkSubscriptionStatus = (userData) => {
    const now = new Date();

    // Admin/SuperAdmin Override
    if (userData.isAdmin || userData.isSuperAdmin) {
        return userData.subscriptionTier; // Trust the role
    }

    // Mega Fan Check (Must have valid time for Super Fan AND Supporter)
    // Actually, simply checking if they have Super Fan capability + the string is enough to not downgrade, 
    // BUT to be robust: Mega Fan = Super Fan Time + Subscriber Time.
    if (userData.subscriptionTier === 'mega_fan') {
        const hasSuperTime = userData.superSubscriberEnd && (userData.superSubscriberEnd.seconds ? new Date(userData.superSubscriberEnd.seconds * 1000) : userData.superSubscriberEnd) > now;
        const hasSubTime = userData.subscriberEnd && (userData.subscriberEnd.seconds ? new Date(userData.subscriberEnd.seconds * 1000) : userData.subscriberEnd) > now;

        if (hasSuperTime && hasSubTime) return 'mega_fan';
        // If they lose one, what happens? SubscriptionSync handles the downgrade?
        // This function is often used for "Auto-Fix". So we should return the fallback.
        if (hasSuperTime) return 'super_subscriber';
        if (hasSubTime) return 'subscriber';
        return 'global';
    }

    // Helper to safe-parse date
    const getDate = (d) => {
        if (!d) return null;
        if (typeof d.toDate === 'function') return d.toDate();
        if (d.seconds) return new Date(d.seconds * 1000); // Fallback
        return d; // Date or string
    };

    // Existing Checks
    if (userData.superSubscriberEnd) {
        const endDate = getDate(userData.superSubscriberEnd);
        if (endDate && endDate > now) return 'super_subscriber';
    }

    if (userData.subscriberEnd) {
        const endDate = getDate(userData.subscriberEnd);
        if (endDate && endDate > now) return 'subscriber';
    }

    return 'global';
};

export const addSubscriptionDays = async (uid, tier, days) => {
    try {
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) throw new Error("User not found");

        const userData = userSnap.data();
        const now = new Date();
        const updates = {};

        // Helper to add days to a date (or Now if date is past/null)
        const addTime = (currentDateFunc) => {
            let baseDate = now;
            if (currentDateFunc) {
                const d = currentDateFunc.seconds ? new Date(currentDateFunc.seconds * 1000) : currentDateFunc;
                if (d > now) baseDate = d;
            }
            const newDate = new Date(baseDate);
            newDate.setDate(newDate.getDate() + days);
            return newDate;
        };

        if (tier === 'super_subscriber') {
            updates.superSubscriberEnd = addTime(userData.superSubscriberEnd);

            // "Subscriber does not deplete while Super Fan is active"
            // If they have valid Subscriber time, we PUSH it forward by the amount of Super Fan time added.
            if (userData.subscriberEnd) {
                const subDate = userData.subscriberEnd.seconds ? new Date(userData.subscriberEnd.seconds * 1000) : userData.subscriberEnd;
                if (subDate > now) {
                    // They are active subscribers. We add 'days' to their subscriber end date
                    // effectively "pausing" it during the super fan period.
                    const newSubDate = new Date(subDate);
                    newSubDate.setDate(newSubDate.getDate() + days);
                    updates.subscriberEnd = newSubDate;
                }
            }
            updates.subscriptionTier = 'super_subscriber';

        } else if (tier === 'subscriber') {
            updates.subscriberEnd = addTime(userData.subscriberEnd);
            // If they are currently Super Subscriber, this just adds to the "backup" subscriber bank.
            // We only update tier if they aren't already Super.
            if (checkSubscriptionStatus({ ...userData, ...updates }) === 'subscriber') {
                updates.subscriptionTier = 'subscriber';
            }
        }

        updates.subscriptionStatus = 'gifted'; // Generalizing to 'gifted'/'active' state for now
        updates.giftNotificationPending = true; // Always notify on add? Or maybe passed as arg. Assuming gift flow for now.

        await updateDoc(userRef, updates);
    } catch (error) {
        console.error("Error adding subscription:", error);
        throw error;
    }
};

export const subscribeToUserProfile = (uid, onUpdate) => {
    return onSnapshot(doc(db, 'users', uid), async (docSnap) => {
        if (docSnap.exists()) {
            const userData = { id: docSnap.id, ...docSnap.data() };

            // Auto-Downgrade Logic
            const effectiveTier = checkSubscriptionStatus(userData);

            if (effectiveTier !== userData.subscriptionTier && userData.subscriptionTier !== 'admin' && userData.subscriptionTier !== 'super_admin') {
                console.warn(`[AUTO-FIX] Would downgrade ${uid} from ${userData.subscriptionTier} to ${effectiveTier}, but disabled to prevent Sync conflicts.`);
                // DISABLED: This conflicts with SubscriptionSync which has more up-to-date data (Stripe API).
                // updateDoc(doc(db, 'users', uid), { subscriptionTier: effectiveTier })
                //     .catch(e => console.error("Error updating tier:", e));

                // Local Optimistic Update (Optional: Keep it for UI consistency if we trust the date check more? No, trust Sync)
                // userData.subscriptionTier = effectiveTier; 
            }

            onUpdate(userData);
        } else {
            onUpdate(null);
        }
    }, (error) => {
        console.error('Error subscribing to user profile:', error);
    });
};

export const getUserByName = async (name) => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('name', '==', name));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            return { id: userDoc.id, ...userDoc.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting user by name:', error);
        throw error;
    }
};

// NEW: Dedicated function to sync name to all past sparks
export const syncNameHistory = async (uid, newName) => {
    try {
        console.log(`Starting manual sync for ${uid} to name: ${newName}`);
        const sparksRef = collection(db, 'sparks');
        const q = query(sparksRef, where('authorId', '==', uid));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            console.log("No sparks found for this user.");
            return 0;
        }

        const batch = writeBatch(db);
        let count = 0;
        let batchCount = 0;

        // Note: Batches are limited to 500. 
        // For a robust solution we should loop batches, but for now we clamp at 490.
        for (const doc of snapshot.docs) {
            if (batchCount < 490) {
                batch.update(doc.ref, { author: newName });
                count++;
                batchCount++;
            }
        }

        await batch.commit();
        console.log(`Successfully synced ${count} sparks.`);
        return count;
    } catch (error) {
        console.error('Error syncing name history:', error);
        throw error;
    }
};

export const updateUserProfile = async (uid, updates) => {
    try {
        await updateDoc(doc(db, 'users', uid), updates);

        // Auto-run sync if name changed (Legacy support, but we also have the button now)
        if (updates.name) {
            // We don't await this to keep the UI fast, but we log errors
            syncNameHistory(uid, updates.name).catch(e => console.error("Auto-sync failed:", e));
        }
    } catch (error) {
        console.error('Error updating user profile:', error);
        throw error;
    }
};

export const getAllUsers = async () => {
    try {
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting all users:', error);
        throw error;
    }
};

// Search users by name (Basic exact or prefix match simulation)
export const searchUsersByName = async (queryText) => {
    try {
        const usersRef = collection(db, 'users');
        const queryNormalized = queryText.toLowerCase();

        // Note: Firestore doesn't support native "includes" or "case-insensitive" search easily without external tools.
        // We will fetch list and filter client-side for this MVP.
        const q = query(usersRef, orderBy('name'), limit(50));
        const querySnapshot = await getDocs(q);

        const users = [];
        querySnapshot.forEach((doc) => {
            const userData = doc.data();
            if (userData.name && userData.name.toLowerCase().includes(queryNormalized)) {
                users.push({ id: doc.id, ...userData });
            }
        });
        return users;
    } catch (error) {
        console.error('Error searching users:', error);
        return [];
    }
};

// Get a random Global user for gifting
export const getRandomGlobalUser = async () => {
    try {
        const usersRef = collection(db, 'users');
        // We want users who are 'global' (free)
        const q = query(
            usersRef,
            where('subscriptionTier', '==', 'global'),
            limit(20) // Get a batch to pick from
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) return null;

        const docs = querySnapshot.docs;
        const randomDoc = docs[Math.floor(Math.random() * docs.length)];
        return { id: randomDoc.id, ...randomDoc.data() };
    } catch (error) {
        console.error('Error getting random global user:', error);
        return null;
    }
};

export const getMuseHistory = async (userId) => {
    try {
        const historyRef = collection(db, 'users', userId, 'history');
        const q = query(historyRef, orderBy('timestamp', 'desc'), limit(20));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting muse history:', error);
        return [];
    }
};

export const uploadAvatar = async (userId, file) => {
    try {
        const storageRef = ref(storage, `avatars/${userId}/${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return downloadURL;
    } catch (error) {
        console.error('Error uploading avatar:', error);
        throw error;
    }
};

export const uploadSparkImage = async (userId, file) => {
    try {
        const timestamp = Date.now();
        // Sanitize filename to avoid weird chars? taking crude approach
        const safeName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
        const storageRef = ref(storage, `sparks/${userId}/${timestamp}_${safeName}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return downloadURL;
    } catch (error) {
        console.error('Error uploading spark image:', error);
        throw error;
    }
};

// ==================== SPARK OPERATIONS ====================

// Helper to calculate streak from activity dates
const calculateStreak = (activityDates) => {
    if (!activityDates || activityDates.size === 0) return 0;

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const todayStr = today.toDateString();
    const yesterdayStr = yesterday.toDateString();

    let currentStreak = 0;
    // Check if there is activity today or yesterday to start the streak
    let checkDate = activityDates.has(todayStr) ? today : (activityDates.has(yesterdayStr) ? yesterday : null);

    if (!checkDate) return 0;

    // Count backwards
    while (activityDates.has(checkDate.toDateString())) {
        currentStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
    }

    return currentStreak;
};

// Helper: Get all activity dates (Sparks + Challenges)
const getAllUserActivityDates = async (userId) => {
    try {
        const dates = new Set();

        // 1. Get Spark Dates
        const sparksQ = query(collection(db, 'sparks'), where('authorId', '==', userId));
        const sparksSnap = await getDocs(sparksQ);
        let totalLikes = 0;
        sparksSnap.forEach(doc => {
            const data = doc.data();
            const d = data.createdAt?.toDate();
            if (d) dates.add(d.toDateString());

            // Sum likes (handle potentially missing or string fields)
            const likes = parseInt(data.likes) || 0;
            totalLikes += likes;
        });

        // 2. Get Completed Challenge Dates
        const challengesQ = query(collection(db, 'users', userId, 'completedChallenges'));
        const challengesSnap = await getDocs(challengesQ);
        challengesSnap.forEach(doc => {
            const d = doc.data().completedAt?.toDate();
            if (d) dates.add(d.toDateString());
        });

        return { dates, sparkCount: sparksSnap.size, challengeCount: challengesSnap.size, totalLikes };
    } catch (e) {
        console.error("Error fetching activity:", e);
        return { dates: new Set(), sparkCount: 0, challengeCount: 0 };
    }
};

export const createSpark = async (sparkData) => {
    try {
        if (!sparkData.authorId) {
            throw new Error("Cannot create spark: Missing author ID");
        }

        const sparksRef = collection(db, 'sparks');
        const docRef = await addDoc(sparksRef, {
            ...sparkData,
            createdAt: serverTimestamp(),
            likes: 0,
            likedBy: [],
            reported: false,
            reportedBy: []
        });

        // Robust user stat update - SELF-HEALING RECOUNT METHOD
        const userRef = doc(db, 'users', sparkData.authorId);

        // Get all activity for Streak + verified counts
        const { dates, sparkCount, challengeCount } = await getAllUserActivityDates(sparkData.authorId);
        const newStreak = calculateStreak(dates);

        console.log(`Self-Healing: Updating stats for ${sparkData.authorId} - Sparks: ${sparkCount}, Streak: ${newStreak}`);

        // Update with verified count and streak
        await setDoc(userRef, {
            stats: {
                sparksCreated: sparkCount,
                streak: newStreak
            }
        }, { merge: true });

        // 5. Daily Challenge Stats & "First to Spark" Logic
        if (sparkData.inspiration) {
            try {
                const historyRef = collection(db, 'dailyChallengeHistory');
                // Find matching challenge
                const challengeQ = query(historyRef, where('prompt', '==', sparkData.inspiration));
                const challengeSnap = await getDocs(challengeQ);

                if (!challengeSnap.empty) {
                    // Sort descending to find most recent
                    const sortedDocs = challengeSnap.docs.sort((a, b) => {
                        const dateA = a.data().createdAt?.seconds || 0;
                        const dateB = b.data().createdAt?.seconds || 0;
                        return dateB - dateA;
                    });

                    const challengeDoc = sortedDocs[0];
                    const challengeData = challengeDoc.data();

                    // FIRST TO SPARK CHECK
                    // MODIFIED: No longer instant. Must earn 3 engagements.
                    if (!challengeData.claimedBy) {
                        console.log(`Challenge linked, but waiting for engagement (First Spark Race).`);

                        // Just increment usage for now
                        await updateDoc(challengeDoc.ref, {
                            usageCount: increment(1)
                        });

                        // SYNC USAGE TO SYSTEM DOC
                        const systemRef = doc(db, 'system', 'dailyChallenge');
                        const systemSnap = await getDoc(systemRef);
                        if (systemSnap.exists() && systemSnap.data().prompt === sparkData.inspiration) {
                            await updateDoc(systemRef, {
                                usageCount: increment(1)
                            });
                        }

                        // We DO NOT set claimedBy or isFirstSpark here anymore.

                    } else {
                        // Just increment usage
                        await updateDoc(challengeDoc.ref, {
                            usageCount: increment(1)
                        });

                        // SYNC USAGE TO SYSTEM DOC
                        const systemRef = doc(db, 'system', 'dailyChallenge');
                        const systemSnap = await getDoc(systemRef);
                        if (systemSnap.exists() && systemSnap.data().prompt === sparkData.inspiration) {
                            await updateDoc(systemRef, {
                                usageCount: increment(1)
                            });
                        }
                    }
                }
            } catch (err) {
                console.error("Error updating challenge stats/claim:", err);
            }
        }

        return { id: docRef.id, ...sparkData, likes: 0, likedBy: [] };
    } catch (error) {
        console.error('Error creating spark:', error);
        throw error;
    }
};

export const deleteSpark = async (sparkId, userId) => {
    try {
        // 1. Get Spark Date before deletion (needed for Challenge cleanup)
        const sparkRef = doc(db, 'sparks', sparkId);
        const sparkSnap = await getDoc(sparkRef);
        let sparkDateStr = null;

        if (sparkSnap.exists()) {
            const data = sparkSnap.data();

            // SOFT DELETE ARCHIVE (Evidence Locker)
            try {
                await setDoc(doc(db, 'deleted_sparks', sparkId), {
                    ...data,
                    deletedAt: serverTimestamp(),
                    originalId: sparkId,
                    deletedBy: userId
                });
            } catch (archiveError) {
                console.error("Error archiving spark:", archiveError);
                // Continue with deletion even if archive fails? 
                // Ideally yes, user wants it gone. 
            }

            if (data.createdAt) {
                sparkDateStr = data.createdAt.toDate().toDateString();
            }

            // 1.5 DECREMENT DAILY CHALLENGE STATS
            if (data.inspiration) {
                try {
                    const historyRef = collection(db, 'dailyChallengeHistory');
                    // NOTE: Removed orderBy to avoid requiring a composite index
                    const challengeQ = query(historyRef, where('prompt', '==', data.inspiration));
                    const challengeSnap = await getDocs(challengeQ);

                    if (!challengeSnap.empty) {
                        // Find the most recent one in memory
                        const sortedDocs = challengeSnap.docs.sort((a, b) => {
                            const dateA = a.data().createdAt?.seconds || 0;
                            const dateB = b.data().createdAt?.seconds || 0;
                            return dateB - dateA; // Descending
                        });

                        const challengeDoc = sortedDocs[0];
                        const challengeData = challengeDoc.data();
                        const count = challengeData.usageCount || 0;
                        const likes = challengeData.totalLikes || 0;

                        const challengeUpdates = {
                            usageCount: Math.max(0, count - 1),
                            totalLikes: Math.max(0, likes - (data.likes || 0))
                        };

                        // FIRST SPARK SUCCESSION LOGIC
                        // Check if this spark was the claimed winner
                        // Robustness: Check SparkID OR (ClaimedBy matches Author AND ClaimedSparkId is missing/null)
                        const isClaimedMatch = challengeData.claimedSparkId === sparkId ||
                            (challengeData.claimedBy === data.authorId && !challengeData.claimedSparkId);

                        if (isClaimedMatch) {
                            console.log("Deleting current First Spark Winner! Initiating Succession...");

                            let qualified = challengeData.qualifiedSparks || [];
                            // Remove self
                            qualified = qualified.filter(q => q.sparkId !== sparkId);

                            if (qualified.length > 0) {
                                // Promote next valid candidate
                                let nextWinner = null;
                                let newQualified = qualified;

                                // Loop until we find a valid winner or run out
                                while (newQualified.length > 0) {
                                    const candidate = newQualified[0];
                                    if (candidate && candidate.sparkId) {
                                        try {
                                            // Attempt to award badge
                                            const nextSparkRef = doc(db, 'sparks', candidate.sparkId);
                                            await updateDoc(nextSparkRef, { isFirstSpark: true });
                                            nextWinner = candidate;
                                            break; // Success!
                                        } catch (e) {
                                            console.warn(`Failed to promote candidate ${candidate.sparkId} (maybe deleted?):`, e);
                                            // Remove failed candidate and try next
                                            newQualified = newQualified.slice(1);
                                        }
                                    } else {
                                        // Invalid candidate data, skip
                                        console.warn("Found invalid candidate in queue", candidate);
                                        newQualified = newQualified.slice(1);
                                    }
                                }

                                if (nextWinner) {
                                    challengeUpdates.claimedBy = nextWinner.authorId;
                                    challengeUpdates.claimedByName = nextWinner.authorName;
                                    challengeUpdates.claimedSparkId = nextWinner.sparkId;
                                    challengeUpdates.claimedAt = serverTimestamp();
                                    challengeUpdates.qualifiedSparks = newQualified;
                                } else {
                                    // No valid successors found after filtering
                                    console.log("No valid successors found. Resetting First Spark.");
                                    challengeUpdates.claimedBy = null;
                                    challengeUpdates.claimedByName = null;
                                    challengeUpdates.claimedSparkId = null;
                                    challengeUpdates.claimedAt = null;
                                    challengeUpdates.qualifiedSparks = [];
                                }

                            } else {
                                // No one left
                                console.log("No successors found. Resetting First Spark.");
                                challengeUpdates.claimedBy = null;
                                challengeUpdates.claimedByName = null;
                                challengeUpdates.claimedSparkId = null;
                                challengeUpdates.claimedAt = null;
                                challengeUpdates.qualifiedSparks = [];
                            }
                        } else {
                            // Using this else block to clean up if we are just a 'runner up' in the queue
                            if (challengeData.qualifiedSparks) {
                                const newQualified = challengeData.qualifiedSparks.filter(q => q.sparkId !== sparkId);
                                if (newQualified.length !== challengeData.qualifiedSparks.length) {
                                    challengeUpdates.qualifiedSparks = newQualified;
                                }
                            }
                        }


                        await updateDoc(challengeDoc.ref, challengeUpdates);

                        // Also Update System Doc if it matches
                        if (data.inspiration) {
                            const systemRef = doc(db, 'system', 'dailyChallenge');
                            const sysSnap = await getDoc(systemRef);
                            // If this was the active challenge, update system doc too to stay in sync
                            if (sysSnap.exists() && sysSnap.data().prompt === data.inspiration) {
                                // Copy only relevant fields
                                const sysUpdates = {};
                                if (challengeUpdates.claimedBy !== undefined) sysUpdates.claimedBy = challengeUpdates.claimedBy;
                                if (challengeUpdates.claimedByName !== undefined) sysUpdates.claimedByName = challengeUpdates.claimedByName;
                                if (challengeUpdates.claimedSparkId !== undefined) sysUpdates.claimedSparkId = challengeUpdates.claimedSparkId;
                                if (challengeUpdates.claimedAt !== undefined) sysUpdates.claimedAt = challengeUpdates.claimedAt;
                                if (challengeUpdates.qualifiedSparks !== undefined) sysUpdates.qualifiedSparks = challengeUpdates.qualifiedSparks;

                                await updateDoc(systemRef, sysUpdates);
                            }
                        }
                    }
                } catch (err) {
                    console.error("Error decrementing challenge stats:", err);
                }
            }
        }

        // 2. Delete the Spark
        await deleteDoc(sparkRef);

        // 3. Challenge Cleanup: If no other sparks exist for this date, remove the Challenge Completion
        if (sparkDateStr) {
            // Check for OTHER sparks on this date
            // Optimization: Fetch all user sparks (simple index) and filter in memory to avoid needing a composite index
            const sparksRef = collection(db, 'sparks');
            const q = query(sparksRef, where('authorId', '==', userId));
            const allSparksSnap = await getDocs(q);

            const hasOtherSparksOnDate = allSparksSnap.docs.some(doc => {
                const d = doc.data().createdAt?.toDate();
                return d && d.toDateString() === sparkDateStr;
            });

            if (!hasOtherSparksOnDate) {
                // No sparks left for this day -> Remove Challenge Completion
                const challengesRef = collection(db, 'users', userId, 'completedChallenges');
                const challengeQ = query(challengesRef, where('dateStr', '==', sparkDateStr));
                const challengeSnap = await getDocs(challengeQ);

                if (!challengeSnap.empty) {
                    console.log(`Self-Healing: removing challenge credit for ${sparkDateStr}`);
                    const deletePromises = challengeSnap.docs.map(doc => deleteDoc(doc.ref));
                    await Promise.all(deletePromises);
                }
            }
        }

        // 4. Robust user stat update - SELF-HEALING RECOUNT METHOD
        const userRef = doc(db, 'users', userId);

        // Get all activity for Streak + verified counts
        const { dates, sparkCount, totalLikes, challengeCount } = await getAllUserActivityDates(userId);
        const newStreak = calculateStreak(dates);

        console.log(`Self-Healing (Delete): Updating stats for ${userId} - Sparks: ${sparkCount}, Likes: ${totalLikes}, Challenges: ${challengeCount}, Streak: ${newStreak}`);

        // Update with verified count
        await setDoc(userRef, {
            stats: {
                sparksCreated: sparkCount,
                likesReceived: totalLikes,
                challengesCompleted: challengeCount,
                streak: newStreak
            }
        }, { merge: true });
    } catch (error) {
        console.error('Error deleting spark:', error);
        throw error;
    }
};

export const getSparks = async (limitCount = 50) => {
    try {
        const sparksRef = collection(db, 'sparks');
        const q = query(sparksRef, orderBy('createdAt', 'desc'), limit(limitCount));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting sparks:', error);
        throw error;
    }
};

export const getSpark = async (sparkId) => {
    try {
        const docRef = doc(db, 'sparks', sparkId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting single spark:', error);
        return null; // Fail gracefully
    }
};

export const getSparksFiltered = async (userId, limitCount = 50) => {
    try {
        const sparksRef = collection(db, 'sparks');
        const q = query(sparksRef, orderBy('createdAt', 'desc'), limit(limitCount));
        const querySnapshot = await getDocs(q);

        // Filter out sparks reported by this user
        return querySnapshot.docs
            .map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
            .filter(spark => !(spark.reportedBy || []).includes(userId));
    } catch (error) {
        console.error('Error getting filtered sparks:', error);
        throw error;
    }
};

export const subscribeToSparks = (userId, onUpdate, limitCount = 50) => {
    try {
        const sparksRef = collection(db, 'sparks');
        const q = query(sparksRef, orderBy('createdAt', 'desc'), limit(limitCount));

        // Return the unsubscribe function
        return onSnapshot(q, (snapshot) => {
            const sparks = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Filter out sparks reported by this user (client-side filter for real-time)
            const filteredSparks = sparks.filter(spark => !(spark.reportedBy || []).includes(userId));

            onUpdate(filteredSparks);
        }, (error) => {
            console.error("Error subscribing to sparks:", error);
        });
    } catch (error) {
        console.error("Error setting up spark subscription:", error);
        throw error;
    }
};

export const getUserSparks = async (userId, limitCount = 50) => {
    try {
        const sparksRef = collection(db, 'sparks');
        // Removing orderBy to avoid needing a composite index (authorId + createdAt)
        const q = query(
            sparksRef,
            where('authorId', '==', userId),
            limit(limitCount)
        );
        const querySnapshot = await getDocs(q);

        const sparks = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Client-side sort
        return sparks.sort((a, b) => {
            const dateA = a.createdAt?.seconds || 0;
            const dateB = b.createdAt?.seconds || 0;
            return dateB - dateA;
        });

    } catch (error) {
        console.error('Error getting user sparks:', error);
        throw error;
    }
};

export const subscribeToUserSparks = (userId, callback) => {
    if (!userId) return () => { };
    const sparksRef = collection(db, 'sparks');
    const q = query(
        sparksRef,
        where('authorId', '==', userId)
    );

    return onSnapshot(q, (snapshot) => {
        const sparks = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Client-side sort (newest first)
        sparks.sort((a, b) => {
            const dateA = a.createdAt?.seconds || 0;
            const dateB = b.createdAt?.seconds || 0;
            return dateB - dateA;
        });

        callback(sparks);
    }, (error) => {
        console.error("Error subscribing to user sparks:", error);
    });
};

export const likeSpark = async (sparkId, userId) => {
    try {
        const sparkRef = doc(db, 'sparks', sparkId);
        const sparkDoc = await getDoc(sparkRef);

        if (sparkDoc.exists()) {
            const data = sparkDoc.data();
            const likedBy = data.likedBy || [];
            const hasLiked = likedBy.includes(userId);
            const authorId = data.authorId; // We need this to update the author's stats

            let newCount = data.likes || 0;

            if (hasLiked) {
                // Unlike
                await updateDoc(sparkRef, {
                    likes: increment(-1),
                    likedBy: arrayRemove(userId)
                });
                newCount = Math.max(0, newCount - 1);
            } else {
                // Like
                await updateDoc(sparkRef, {
                    likes: increment(1),
                    likedBy: arrayUnion(userId)
                });
                newCount = newCount + 1;
            }

            // SELF-HEALING: Recalculate Author's Total Likes
            // Query all sparks by this author and sum up their likes
            if (authorId) {
                const q = query(collection(db, 'sparks'), where('authorId', '==', authorId));
                const querySnapshot = await getDocs(q);

                let totalLikes = 0;
                querySnapshot.forEach(doc => {
                    const sparkLikes = doc.data().likes || 0;
                    // If this is the spark we just modified, use the NEW count because 
                    // the query result might be stale (eventual consistency) 
                    // OR it might be fresh. 
                    // Local writes are usually immediately visible. 
                    // But to be absolutely safe, let's trust the read unless it's the one we just toggled?
                    // Actually, since we awaited updateDoc, the read SHOULD represent the new state.
                    totalLikes += sparkLikes;
                });

                console.log(`Self-Healing (Likes): Updating total likes for ${authorId} to verified sum: ${totalLikes}`);

                const userRef = doc(db, 'users', authorId);
                await setDoc(userRef, {
                    stats: {
                        likesReceived: totalLikes
                    }
                }, { merge: true });
            }

            // SELF-HEALING: Update Daily Challenge 'totalLikes' & CHECK WIN CONDITION
            if (data.inspiration) {
                try {
                    const sysChallengeRef = doc(db, 'system', 'dailyChallenge');
                    const sysSnap = await getDoc(sysChallengeRef);

                    if (sysSnap.exists()) {
                        const sysData = sysSnap.data();
                        // Verify this spark is for the CURRENT active challenge (simplification for "The Race")
                        // If users are liking old challenge sparks, they don't retrospectively win.
                        if (sysData.prompt === data.inspiration) {

                            // 1. Update total likes on System Doc (or History if we tracked it there primarily)
                            // Note: Original code updated History doc. Let's keep that but ALSO check win.

                            // Update History Doc Stats
                            const historyRef = collection(db, 'dailyChallengeHistory');
                            const challengeQ = query(historyRef, where('prompt', '==', data.inspiration), limit(1));
                            const challengeSnap = await getDocs(challengeQ);

                            if (!challengeSnap.empty) {
                                await updateDoc(challengeSnap.docs[0].ref, {
                                    totalLikes: increment(hasLiked ? -1 : 1)
                                });
                            }

                            // 2. CHECK WIN CONDITION (First Spark)
                            // Rule: Link + Not Claimed + (Likes + Reacts >= 3)
                            // 2. CHECK WIN CONDITION & QUALIFICATION QUEUE
                            // Rule: Link + (Likes + Reacts >= 3) -> Add to Queue
                            // If Unclaimed -> Claim

                            const currentLikes = newCount; // Adjusted like count
                            let currentReacts = 0;
                            if (data.reactions) {
                                Object.values(data.reactions).forEach(arr => currentReacts += arr.length);
                            }

                            const totalInteractions = currentLikes + currentReacts;

                            if (totalInteractions >= 3) {
                                console.log("Spark Reached Threshold (3+ interactions). Checking First Spark Status...");

                                // Candidate Object
                                const candidate = {
                                    sparkId: sparkId,
                                    authorId: authorId,
                                    authorName: data.author || 'A Creator',
                                    qualifiedAt: Date.now()
                                };

                                // Get existing queue
                                const currentQueue = sysData.qualifiedSparks || [];
                                // Avoid duplicates
                                const isQueued = currentQueue.some(q => q.sparkId === sparkId);
                                let newQueue = currentQueue;

                                if (!isQueued) {
                                    newQueue = [...currentQueue, candidate].sort((a, b) => a.qualifiedAt - b.qualifiedAt);
                                }

                                const claimUpdate = {
                                    qualifiedSparks: newQueue
                                };

                                // If NO ONE has claimed it yet, or if we are just updating the queue
                                if (!sysData.claimedBy) {
                                    console.log("...And it is UNCLAIMED! Awarding First Spark Badge.");
                                    claimUpdate.claimedBy = authorId;
                                    claimUpdate.claimedByName = data.author || 'A Creator';
                                    claimUpdate.claimedSparkId = sparkId; // NEW: Track specific spark
                                    claimUpdate.claimedAt = serverTimestamp();

                                    // Update Spark Badge
                                    await updateDoc(sparkRef, { isFirstSpark: true });
                                } else {
                                    console.log("...But it is already claimed. Added to succession queue.");
                                }

                                // A. Update System Doc
                                await updateDoc(sysChallengeRef, claimUpdate);

                                // B. Update History Doc
                                if (!challengeSnap.empty) {
                                    await updateDoc(challengeSnap.docs[0].ref, claimUpdate);
                                }
                            }
                        }
                    }
                } catch (err) {
                    console.error("Error updating challenge total likes / checking win:", err);
                }
            }

            return { liked: !hasLiked, newCount: newCount };
        }
        return null;
    } catch (error) {
        console.error('Error liking spark:', error);
        throw error;
    }
};

export const reportSpark = async (sparkId, userId) => {
    try {
        const sparkRef = doc(db, 'sparks', sparkId);
        await updateDoc(sparkRef, {
            reportedBy: arrayUnion(userId),
            reported: true,
            lastReportedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error reporting spark:', error);
        throw error;
    }
};

export const subscribeToReportedSparks = (callback) => {
    const sparksRef = collection(db, 'sparks');
    const q = query(sparksRef, where('reported', '==', true));

    return onSnapshot(q, (snapshot) => {
        const sparks = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        // Sort by report time or creation time
        sparks.sort((a, b) => {
            const timeA = a.lastReportedAt?.seconds || a.createdAt?.seconds || 0;
            const timeB = b.lastReportedAt?.seconds || b.createdAt?.seconds || 0;
            return timeB - timeA;
        });
        callback(sparks);
    });
};

export const resolveSparkReport = async (sparkId, action) => {
    // action: 'dismiss' (keep spark, remove flag) or 'delete' (remove spark)
    try {
        const sparkRef = doc(db, 'sparks', sparkId);

        if (action === 'delete') {
            // Fetch spark to get authorId for stat updates
            const sparkSnap = await getDoc(sparkRef);
            if (sparkSnap.exists()) {
                const sparkData = sparkSnap.data();
                // Use the shared deleteSpark function to ensure stats are synced
                await deleteSpark(sparkId, sparkData.authorId);
            } else {
                // Already deleted? Just cleanup
                console.warn("Spark already deleted or not found");
                await deleteDoc(sparkRef);
            }
        } else {
            await updateDoc(sparkRef, {
                reported: false,
                reportedBy: [] // Clear reporters? Or keep history? clearing for now.
            });
        }
    } catch (error) {
        console.error('Error resolving spark report:', error);
        throw error;
    }
};

export const isSparkLiked = async (sparkId, userId) => {
    try {
        const sparkRef = doc(db, 'sparks', sparkId);
        const sparkDoc = await getDoc(sparkRef);

        if (sparkDoc.exists()) {
            const likedBy = sparkDoc.data().likedBy || [];
            return likedBy.includes(userId);
        }
        return false;
    } catch (error) {
        console.error('Error checking if spark is liked:', error);
        return false;
    }
};

// ==================== FRIEND OPERATIONS ====================

export const sendFriendRequest = async (fromUserId, fromUserName, fromAvatar, toUserId, toUserName) => {
    try {
        // Check if friendship already exists
        const existingFriendship = await checkFriendship(fromUserId, toUserId);
        if (existingFriendship) {
            throw new Error('Already friends');
        }

        // Check if request already sent
        const friendRequestsRef = collection(db, 'friendRequests');
        const q = query(
            friendRequestsRef,
            where('from', '==', fromUserId),
            where('to', '==', toUserId),
            where('status', '==', 'pending')
        );
        const existing = await getDocs(q);
        if (!existing.empty) {
            throw new Error('Friend request already sent');
        }

        await addDoc(friendRequestsRef, {
            from: fromUserId,
            fromName: fromUserName,
            fromAvatar: fromAvatar,
            to: toUserId,
            toName: toUserName,
            status: 'pending',
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error sending friend request:', error);
        throw error;
    }
};

export const cancelFriendRequest = async (fromUserId, toUserId) => {
    try {
        const friendRequestsRef = collection(db, 'friendRequests');
        const q = query(
            friendRequestsRef,
            where('from', '==', fromUserId),
            where('to', '==', toUserId),
            where('status', '==', 'pending')
        );

        const querySnapshot = await getDocs(q);

        const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);

        return true;
    } catch (error) {
        console.error('Error cancelling friend request:', error);
        throw error;
    }
};

export const getFriendRequests = async (userId) => {
    try {
        const friendRequestsRef = collection(db, 'friendRequests');
        const q = query(friendRequestsRef,
            where('to', '==', userId),
            where('status', '==', 'pending')
        );
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting friend requests:', error);
        throw error;
    }
};

export const acceptFriendRequest = async (requestId, fromUserId, toUserId) => {
    try {
        // Update request status
        await updateDoc(doc(db, 'friendRequests', requestId), {
            status: 'accepted'
        });

        // Create friendship document
        const friendshipRef = collection(db, 'friends');
        await addDoc(friendshipRef, {
            users: [fromUserId, toUserId],
            status: 'active',
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error accepting friend request:', error);
        throw error;
    }
};

export const declineFriendRequest = async (requestId) => {
    try {
        await updateDoc(doc(db, 'friendRequests', requestId), {
            status: 'declined'
        });
    } catch (error) {
        console.error('Error declining friend request:', error);
        throw error;
    }
};

export const getFriends = async (userId) => {
    try {
        const friendsRef = collection(db, 'friends');
        const q = query(
            friendsRef,
            where('users', 'array-contains', userId),
            where('status', '==', 'active')
        );
        const querySnapshot = await getDocs(q);

        const friendIds = [];
        querySnapshot.docs.forEach(doc => {
            const users = doc.data().users;
            const friendId = users.find(id => id !== userId);
            if (friendId) friendIds.push({ id: friendId, friendshipId: doc.id });
        });

        // Get friend profiles
        const friends = [];
        for (const { id: friendId, friendshipId } of friendIds) {
            const friendProfile = await getUserProfile(friendId);
            if (friendProfile) {
                friends.push({ ...friendProfile, friendshipId });
            }
        }

        return friends;
    } catch (error) {
        console.error('Error getting friends:', error);
        throw error;
    }
};

export const removeFriend = async (friendshipId, hostId, friendId) => {
    try {
        // 1. Remove Friendship
        await updateDoc(doc(db, 'friends', friendshipId), {
            status: 'removed',
            removedAt: serverTimestamp()
        });

        // 2. Remove Friend from any Private Chats hosted by 'hostId'
        if (hostId && friendId) {
            const chatsRef = collection(db, 'chats');
            const q = query(
                chatsRef,
                where('hostId', '==', hostId),
                where('participantIds', 'array-contains', friendId),
                where('type', '==', 'private')
            );

            const snapshot = await getDocs(q);
            const batch = writeBatch(db);

            snapshot.docs.forEach(doc => {
                const chatRef = doc.ref;
                batch.update(chatRef, {
                    participantIds: arrayRemove(friendId),
                    [`participants.${friendId}`]: deleteField()
                });
            });

            if (!snapshot.empty) {
                await batch.commit();
            }
        }
    } catch (error) {
        console.error('Error removing friend:', error);
        throw error;
    }
};

export const checkFriendship = async (userId1, userId2) => {
    try {
        const friendsRef = collection(db, 'friends');
        const q = query(
            friendsRef,
            where('users', 'array-contains', userId1),
            where('status', '==', 'active')
        );
        const querySnapshot = await getDocs(q);

        for (const doc of querySnapshot.docs) {
            if (doc.data().users.includes(userId2)) {
                return true;
            }
        }
        return false;
    } catch (error) {
        console.error('Error checking friendship:', error);
        return false;
    }
};

// ==================== CHAT OPERATIONS ====================

// --- Private Chat Management ---

export const createPrivateChat = async (hostId, friendId) => {
    try {
        const chatsRef = collection(db, 'chats');

        // Initial participants map with joinedAt
        const participants = {};
        const now = serverTimestamp();
        participants[hostId] = { joinedAt: now, role: 'host', unreadCount: 0 };
        participants[friendId] = { joinedAt: now, role: 'member', unreadCount: 0 };

        const docRef = await addDoc(chatsRef, {
            type: 'private',
            hostId,
            participantIds: [hostId, friendId],
            participants,
            createdAt: now,
            updatedAt: now,
            lastMessage: null
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating private chat:', error);
        throw error;
    }
};

export const createGroupChat = async (hostId, friendIds) => {
    try {
        const chatsRef = collection(db, 'chats');
        const now = serverTimestamp();
        const participants = {};

        // Add host
        participants[hostId] = { joinedAt: now, role: 'host', unreadCount: 0 };

        // Add all friends
        friendIds.forEach(fid => {
            participants[fid] = { joinedAt: now, role: 'member', unreadCount: 0 };
        });

        // Unique IDs
        const allParticipantIds = Array.from(new Set([hostId, ...friendIds]));

        const docRef = await addDoc(chatsRef, {
            type: 'private',
            hostId,
            participantIds: allParticipantIds,
            participants,
            createdAt: now,
            updatedAt: now,
            lastMessage: null
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating group chat:', error);
        throw error;
    }
};

export const deletePrivateChat = async (chatId) => {
    try {
        await deleteDoc(doc(db, 'chats', chatId));
    } catch (error) {
        console.error('Error deleting private chat:', error);
        throw error;
    }
};

export const addParticipantToChat = async (chatId, newUserId) => {
    try {
        const chatRef = doc(db, 'chats', chatId);
        const now = new Date(); // Use client time for immediate UI feedback if needed, but serverTimestamp is better for consistency.
        // We need serverTimestamp for the query comparison though.

        await updateDoc(chatRef, {
            participantIds: arrayUnion(newUserId),
            [`participants.${newUserId}`]: {
                joinedAt: serverTimestamp(),
                role: 'member'
            }
        });
    } catch (error) {
        console.error('Error adding participant:', error);
        throw error;
    }
};
export const subscribeToUserChats = (userId, callback) => {
    try {
        const chatsRef = collection(db, 'chats');
        // Query chats where I am a participant
        // Note: We avoid server-side sorting to prevent needing a manual composite index creation (array-contains + orderBy).
        const q = query(
            chatsRef,
            where('participantIds', 'array-contains', userId)
        );

        return onSnapshot(q, (snapshot) => {
            const chats = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Client-side sort
            chats.sort((a, b) => {
                const timeA = a.updatedAt?.seconds || 0;
                const timeB = b.updatedAt?.seconds || 0;
                return timeB - timeA;
            });
            callback(chats);
        });
    } catch (error) {
        console.error('Error subscribing to user chats:', error);
        // Fallback for empty list if query fails (e.g. index missing)
        callback([]);
    }
};

// --- Message Operations ---

export const sendMessage = async (chatId, senderId, senderName, content, senderTier = null) => {
    try {
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const now = serverTimestamp();

        // 1. Add Message
        await addDoc(messagesRef, {
            senderId,
            senderName,
            senderTier, // Save tier for badges
            content,
            deletedFor: [],
            timestamp: now
        });

        // 2. Update Chat Metadata (for list preview & sorting)
        // Only if it's not the general room (which doesn't have a parent doc in 'chats' collection usually, 
        // OR we need to make sure 'general-room' exists in 'chats' if we want unified list.
        // For now, only update if it exists).
        if (chatId !== 'general-room') {
            try {
                const chatRef = doc(db, 'chats', chatId);
                const chatSnap = await getDoc(chatRef);

                const updates = {
                    lastMessage: {
                        content,
                        senderId,
                        senderTier,
                        timestamp: now
                    },
                    updatedAt: now
                };

                if (chatSnap.exists()) {
                    const pIds = chatSnap.data().participantIds || [];
                    pIds.forEach(pid => {
                        if (pid !== senderId) {
                            updates[`participants.${pid}.unreadCount`] = increment(1);
                        }
                    });
                }

                await updateDoc(chatRef, updates);
            } catch (metaError) {
                console.warn('Failed to update chat metadata (non-critical):', metaError);
                // Continue execution so the message is still considered "sent"
            }
        }

    } catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
};

export const subscribeToMessages = (chatId, userId, callback, limitCount = 50) => {
    try {
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(limitCount));

        return onSnapshot(q, (snapshot) => {
            const messages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Filter deleted messages
            const visibleMessages = messages.filter(msg => !(msg.deletedFor || []).includes(userId));
            callback(visibleMessages);
        });
    } catch (error) {
        console.error('Error subscribing to messages:', error);
        throw error;
    }
};

export const cleanupOldMessages = async (daysToKeep = 3) => {
    try {
        // Optimization: Helper check (though rules strictly enforce it)
        // We can't easily check admin status here without an extra read, so we'll rely on try-catch
        // OR we can pass the user object if available. For now, rely on try-catch.
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

        // Query all chats (since we want to clean up database-wide storage) - BUT access rules might be complex.
        // For this app, messages are stored in subcollections of 'chats'.
        // To delete effectively from CLIENT side, we'd need to know which chats to check.
        // We know the main room is `general-room`.

        const messagesRef = collection(db, 'chats', 'general-room', 'messages');
        const q = query(messagesRef, where('timestamp', '<', cutoffDate));

        const snapshot = await getDocs(q);

        if (snapshot.empty) return;

        console.log(`Cleaning up ${snapshot.size} old messages from general-room...`);

        const batch = writeBatch(db);
        snapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
        });

        await batch.commit();
        console.log('Cleanup complete.');
    } catch (error) {
        console.error('Error cleaning up old messages:', error);
    }
};

export const markChatAsRead = async (chatId, userId) => {
    try {
        const chatRef = doc(db, 'chats', chatId);
        await updateDoc(chatRef, {
            [`participants.${userId}.unreadCount`]: 0
        });
    } catch (error) {
        console.error('Error marking chat as read:', error);
    }
};

export const getMessages = async (chatId, userId, limitCount = 50) => {
    try {
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(limitCount));
        const querySnapshot = await getDocs(q);

        // Filter out messages deleted by this user
        return querySnapshot.docs
            .map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
            .filter(msg => !(msg.deletedFor || []).includes(userId));
    } catch (error) {
        console.error('Error getting messages:', error);
        throw error;
    }
};

export const deleteMessage = async (chatId, messageId, userId, isAuthor) => {
    try {
        const messageRef = doc(db, 'chats', chatId, 'messages', messageId);

        if (isAuthor) {
            // Unsend: Delete message for everyone
            await deleteDoc(messageRef);
        } else {
            // Delete for me: Add userId to deletedFor array
            await updateDoc(messageRef, {
                deletedFor: arrayUnion(userId)
            });
        }
    } catch (error) {
        console.error('Error deleting message:', error);
        throw error;
    }
};

// ==================== DAILY CHALLENGE OPERATIONS ====================

const DAILY_PROMPTS = [
    "Reshape an object using only triangles.",
    "Draw the sound of a closing door.",
    "Design a plant that grows on the moon.",
    "Write a poem that rhymes with 'Orange' (Good luck!).",
    "Photograph a reflection that looks like a portal.",
    "Invent a new color and describe it.",
    "Sketch your favorite song.",
    "Create a character who is made of liquid.",
    "What if gravity was sideways?",
    "Design a mask for a time traveler.",
    "Draw something invisible.",
    "Visualize the concept of 'Silence'.",
    "Design a house for a squirrel.",
    "Paint a landscape using coffee or tea (digital or real).",
    "Create a flag for the internet."
];

import { generateDailyChallengeAI } from './gemini.js';

export const regenerateDailyChallenge = async () => {
    try {
        console.log("Generating new Daily Challenge (AI Check)...");
        const challengeDocRef = doc(db, 'system', 'dailyChallenge');

        // AI Generation attempt
        let promptText = await generateDailyChallengeAI();

        // Fallback to static list if AI fails (or key missing)
        if (!promptText) {
            console.log("AI failed or not configured, using static lists.");
            promptText = DAILY_PROMPTS[Math.floor(Math.random() * DAILY_PROMPTS.length)];
        }

        const todayStr = new Date().toDateString();

        const newChallenge = {
            prompt: promptText,
            date: todayStr,
            createdAt: serverTimestamp(),
            totalLikes: 0,
            usageCount: 0,
            claimedBy: null,
            claimedByName: null,
            claimedAt: null
        };

        // Overwrite
        await setDoc(challengeDocRef, newChallenge);

        // ADD TO HISTORY
        await addDoc(collection(db, 'dailyChallengeHistory'), newChallenge);

        return { id: 'dailyChallenge', ...newChallenge };
    } catch (error) {
        console.error('Error regenerating daily challenge:', error);
        throw error;
    }
};

export const getDailyChallenge = async () => {
    try {
        const challengeDocRef = doc(db, 'system', 'dailyChallenge');
        const challengeDoc = await getDoc(challengeDocRef);
        const today = new Date();
        const todayStr = today.toDateString();

        if (challengeDoc.exists()) {
            const data = challengeDoc.data();
            // Check if it's still today's challenge
            if (data.date === todayStr) {
                return { id: challengeDoc.id, ...data };
            }
        }

        // Needs update!
        // Needs update!
        console.log("Daily Challenge is stale or missing. Generating new one...");
        const newChallenge = await regenerateDailyChallenge();
        return newChallenge;
    } catch (error) {
        console.error('Error getting daily challenge:', error);
        return null;
    }
};

export const subscribeToDailyChallenge = (onUpdate) => {
    try {
        const challengeDocRef = doc(db, 'system', 'dailyChallenge');

        // Immediate check/update via getDailyChallenge to ensure we have *today's* challenge created if needed
        // We do this just once on mount effectively by calling it here, though it's async.
        // The snapshot listener below will catch the result of this if it writes a new doc.
        getDailyChallenge().catch(e => console.error("Error in initial challenge check:", e));

        return onSnapshot(challengeDocRef, (docSnap) => {
            if (docSnap.exists()) {
                onUpdate({ id: docSnap.id, ...docSnap.data() });
            } else {
                onUpdate(null);
            }
        }, (error) => {
            console.error("Error subscribing to daily challenge:", error);
        });
    } catch (error) {
        console.error("Error setting up daily challenge subscription:", error);
        throw error;
    }
};



export const getDailyChallengeHistory = async () => {
    try {
        const historyRef = collection(db, 'dailyChallengeHistory');
        const q = query(historyRef, orderBy('createdAt', 'desc'), limit(20)); // Limit to last 20
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting daily challenge history:', error);
        return [];
    }
};

export const subscribeToDailyChallengeHistory = (onUpdate) => {
    const historyRef = collection(db, 'dailyChallengeHistory');
    const q = query(historyRef, orderBy('createdAt', 'desc'), limit(20));

    return onSnapshot(q, (snapshot) => {
        const history = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        onUpdate(history);
    }, (error) => {
        console.error("Error subscribing to challenge history:", error);
    });
};

export const restoreDailyChallenge = async (pastChallenge) => {
    try {
        console.log("Restoring past challenge:", pastChallenge.prompt);
        const challengeDocRef = doc(db, 'system', 'dailyChallenge');

        // We use the PAST challenge's prompt, but TODAY'S date so it counts as active
        const restoredChallenge = {
            prompt: pastChallenge.prompt,
            date: new Date().toDateString(),
            createdAt: serverTimestamp(),
            restoredFromId: pastChallenge.id
        };

        await setDoc(challengeDocRef, restoredChallenge);

        // Optional: Do we add a "Restored" record to history? 
        // Let's NOT, to avoid cluttering it with duplicates of the same prompt.

        return { id: 'dailyChallenge', ...restoredChallenge };
    } catch (error) {
        console.error('Error restoring daily challenge:', error);
        throw error;
    }
};

export const completeDailyChallenge = async (userId) => {
    try {
        const todayStr = new Date().toDateString();

        // Check if already completed today
        const completedRef = collection(db, 'users', userId, 'completedChallenges');
        const q = query(completedRef, where('dateStr', '==', todayStr));
        const existing = await getDocs(q);

        if (!existing.empty) {
            console.log("Challenge already completed today.");
            return; // Idempotent
        }

        // Record completion
        await addDoc(completedRef, {
            dateStr: todayStr,
            completedAt: serverTimestamp(),
            challengeId: 'daily'
        });

        // Recalculate Totals & Streak
        const { dates, sparkCount, challengeCount } = await getAllUserActivityDates(userId);
        const newStreak = calculateStreak(dates);

        console.log(`Self-Healing (Challenge): Updating for ${userId} - Completed: ${challengeCount}, Streak: ${newStreak}`);

        await setDoc(doc(db, 'users', userId), {
            stats: {
                challengesCompleted: challengeCount,
                streak: newStreak
            }
        }, { merge: true });

    } catch (error) {
        console.error('Error completing daily challenge:', error);
        throw error;
    }
};

export const createDailyChallenge = async (prompt) => {
    try {
        await setDoc(doc(db, 'system', 'dailyChallenge'), {
            prompt,
            date: new Date().toLocaleDateString(),
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error creating daily challenge:', error);
        throw error;
    }
};

// ==================== MUSE AI OPERATIONS ====================

export const checkAndIncrementMuseUsage = async (userId, tier, isAdmin, isSuperAdmin) => {
    try {
        if (isSuperAdmin) {
            return { allowed: true, remaining: 9999, limit: 9999 };
        }

        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
            throw new Error("User not found");
        }

        const userData = userDoc.data();
        const dailyUsage = userData.dailyMuseUsage || { date: '', count: 0 };
        const todayStr = new Date().toDateString();

        // Determine Limit
        // Determine Limit
        let limit = 5; // Global
        if (isAdmin) limit = 30;
        else if (tier === 'super_subscriber' || tier === 'superSubscriber') limit = 20;
        else if (tier === 'subscriber') limit = 10;

        // Reset if new day
        if (dailyUsage.date !== todayStr) {
            await updateDoc(userRef, {
                dailyMuseUsage: { date: todayStr, count: 1 },
                'stats.museUsage': increment(1)
            });
            return { allowed: true, remaining: limit - 1, limit };
        }

        // Check limit
        if (dailyUsage.count >= limit) {
            return { allowed: false, remaining: 0, limit };
        }

        // Increment
        await updateDoc(userRef, {
            'dailyMuseUsage.count': increment(1),
            'stats.museUsage': increment(1)
        });

        return { allowed: true, remaining: limit - (dailyUsage.count + 1), limit };

    } catch (error) {
        console.error("Error checking Muse usage:", error);
        throw error; // Let frontend handle error
    }
};

// Check Daily Spark Limit
export const checkDailySparkLimit = async (userId, tier, isAdmin, isSuperAdmin) => {
    if (!userId) return { allowed: false, limit: 0, count: 0 };

    // 1. Determine Limit
    let limit = 5; // Global default
    if (isSuperAdmin) limit = 9999;
    else if (isAdmin) limit = 30; // Admin
    else if (tier === 'mega_fan') limit = 30;
    else if (tier === 'super_subscriber' || tier === 'super_fan') limit = 20;
    else if (tier === 'subscriber') limit = 10;

    // 2. Count Today's Sparks
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const q = query(
        collection(db, 'sparks'),
        where('authorId', '==', userId),
        where('createdAt', '>=', startOfToday)
    );

    const snapshot = await getCountFromServer(q);
    const count = snapshot.data().count;

    return {
        allowed: count < limit,
        limit,
        count
    };
};

// ==================== BADGE & STATS OPERATIONS ====================

export const BADGES = [
    { id: 'vip_gold_crown', name: 'Hidden Gold Crown', description: 'Awarded to Mega Fans with dual subscriptions.', icon: 'Crown', color: '#FFD700' },
    { id: 'early_adopter', name: 'Early Adopter', icon: 'Rocket', color: '#fbbf24', description: 'Join during the Beta phase', unlockedDescription: 'Joined during the Beta phase!' },
    { id: 'on_fire', name: 'On Fire', icon: 'Flame', color: '#ef4444', description: 'Reach a 3-day streak', unlockedDescription: 'Reached a 3-day streak!' },
    { id: 'first_spark', name: 'First Spark', icon: 'Sparkles', color: '#8b5cf6', description: 'Create your first Spark', unlockedDescription: 'Posted first Spark!' },
    { id: 'muse_master', name: 'Muse Master', icon: 'Award', color: '#ec4899', description: 'Use Muse 5 times', unlockedDescription: 'Used Muse 5 times!' },
    { id: 'social_butterfly', name: 'Social Butterfly', icon: 'Users', color: '#06b6d4', description: 'Receive 10 Likes', unlockedDescription: 'Received 10 Likes!' },
    { id: 'challenge_champion', name: 'Challenge Champion', icon: 'Trophy', color: '#f59e0b', description: 'Complete 3 Daily Challenges', unlockedDescription: 'Completed 3 Daily Challenges!' },
    { id: 'spark_genius', name: 'Spark of Genius', icon: 'Lightbulb', color: '#ffd700', description: 'Create 10 Sparks', unlockedDescription: 'Created 10 Sparks!' }
];

export const checkAndUnlockBadges = async (userId) => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) return [];

        const userData = userDoc.data();
        const stats = userData.stats || {};
        const badges = userData.badges || [];
        const newUnlocks = [];

        // Define badge unlock conditions
        // Define badge unlock conditions (Source of Truth)
        const badgeConditions = {
            'muse_master': (stats.museUsage || 0) >= 5, // New Rule
            'spark_genius': (stats.sparksCreated || 0) >= 10, // Changed from muse usage to expert creator
            'social_butterfly': (stats.likesReceived || 0) >= 10,
            'challenge_champion': (stats.challengesCompleted || 0) >= 3,
            'on_fire': (stats.streak || 0) >= 3, // Lowered for testing
            'first_spark': (stats.sparksCreated || 0) >= 1
        };

        // CORRECTION: Clean up duplicates and obsolete badges first
        // This fixes the issue where "Locked" versions exist alongside "Unlocked" ones.
        const updatedBadgesMap = new Map();
        let databaseDirty = false;

        // 1. Process existing badges (De-duplicate & Remove obsolete)
        badges.forEach(badge => {
            // Is this a valid badge ID?
            const isValid = BADGES.some(def => def.id === badge.id);
            if (!isValid) {
                databaseDirty = true; // Remove obsolete badge (e.g. streak_master)
                return;
            }

            const existing = updatedBadgesMap.get(badge.id);
            if (existing) {
                databaseDirty = true; // Found duplicate
                // Keep the one that is unlocked
                if (badge.unlocked && !existing.unlocked) {
                    updatedBadgesMap.set(badge.id, badge);
                }
            } else {
                updatedBadgesMap.set(badge.id, badge);
            }
        });

        // 2. Check for NEW unlocks based on conditions
        for (const [badgeId, condition] of Object.entries(badgeConditions)) {
            const existingBadge = updatedBadgesMap.get(badgeId);
            const isUnlocked = existingBadge?.unlocked;
            const badgeDef = BADGES.find(b => b.id === badgeId);

            if (badgeDef && !isUnlocked && condition) {
                const newBadge = { ...badgeDef, unlocked: true, unlockedAt: new Date().toISOString() };
                updatedBadgesMap.set(badgeId, newBadge);
                newUnlocks.push(newBadge);
                databaseDirty = true;
            }
        }

        // 3. Save if any changes (cleanup or new unlocks)
        if (databaseDirty) {
            console.log("Self-Healing Badges: Updating user badge list...");
            const allBadges = Array.from(updatedBadgesMap.values());
            await updateDoc(doc(db, 'users', userId), {
                badges: allBadges
            });
        }

        return newUnlocks;
    } catch (error) {
        console.error('Error checking badges:', error);
        throw error;
    }
};

export const updateUserStats = async (userId, statUpdates) => {
    try {
        await updateDoc(doc(db, 'users', userId), statUpdates);
    } catch (error) {
        console.error('Error updating user stats:', error);
        throw error;
    }
};

export const getTopUsers = async (sortBy = 'sparksCreated', limitCount = 10) => {
    try {
        const usersRef = collection(db, 'users');
        // Note: You need a composite index for this if you filter too, but here simple order is fine.
        const q = query(
            usersRef,
            orderBy(`stats.${sortBy}`, 'desc'),
            limit(limitCount)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting top users:', error);
        return [];
    }
};

// ==================== MAGIC SPARK OPERATIONS ====================

export const getMagicSparkHolder = async () => {
    try {
        const magicSparkDoc = await getDoc(doc(db, 'system', 'magicSpark'));
        if (magicSparkDoc.exists()) {
            return magicSparkDoc.data();
        }
        return null;
    } catch (error) {
        console.error('Error getting magic spark holder:', error);
        return null;
    }
};

export const setMagicSparkHolder = async (userId, holderName) => {
    try {
        const currentData = await getMagicSparkHolder();
        const history = currentData?.history || [];

        // Add current holder to history if exists
        if (currentData?.currentHolder && currentData.currentHolder !== userId) {
            history.unshift(currentData.currentHolder);
        }

        await setDoc(doc(db, 'system', 'magicSpark'), {
            currentHolder: userId,
            holderName,
            awardedDate: serverTimestamp(),
            history: history.slice(0, 3) // Keep only last 3 holders
        });
    } catch (error) {
        console.error('Error setting magic spark holder:', error);
        throw error;
    }
};

export const checkAndAwardMagicSpark = async () => {
    try {
        const magicSparkData = await getMagicSparkHolder();

        if (!magicSparkData) return null;

        const lastAwardDate = magicSparkData.awardedDate?.toDate() || new Date(0);
        const daysSinceAward = (Date.now() - lastAwardDate.getTime()) / (1000 * 60 * 60 * 24);

        if (daysSinceAward >= 7) {
            // Get all sparks from the past week
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

            const sparksRef = collection(db, 'sparks');
            const q = query(sparksRef, where('createdAt', '>=', oneWeekAgo));
            const querySnapshot = await getDocs(q);

            // Get unique authors (excluding system authors)
            const authors = new Set();
            querySnapshot.docs.forEach(doc => {
                const authorId = doc.data().authorId;
                if (authorId && authorId !== 'system') {
                    authors.add(authorId);
                }
            });

            // Exclude current holder and recent history
            const excludedUsers = [
                magicSparkData.currentHolder,
                ...(magicSparkData.history || []).slice(0, 1)
            ];

            const eligibleAuthors = [...authors].filter(id => !excludedUsers.includes(id));

            if (eligibleAuthors.length > 0) {
                const randomIndex = Math.floor(Math.random() * eligibleAuthors.length);
                const newHolderId = eligibleAuthors[randomIndex];

                // Get holder name
                const holderDoc = await getDoc(doc(db, 'users', newHolderId));
                const holderName = holderDoc.exists() ? holderDoc.data().name : 'Unknown';

                await setMagicSparkHolder(newHolderId, holderName);
                return newHolderId;
            }
        }

        return null;
    } catch (error) {
        console.error('Error checking and awarding magic spark:', error);
        return null;
    }
};

export const subscribeToFriendRequests = (userId, callback) => {
    if (!userId) return () => { };
    const q = query(
        collection(db, 'friendRequests'),
        where('to', '==', userId),
        where('status', '==', 'pending')
    );
    return onSnapshot(q, (snapshot) => {
        const requests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        callback(requests);
    });
};

export const subscribeToSentFriendRequests = (userId, callback) => {
    if (!userId) return () => { };
    const q = query(
        collection(db, 'friendRequests'),
        where('from', '==', userId),
        where('status', '==', 'pending')
    );
    return onSnapshot(q, (snapshot) => {
        const requests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        callback(requests);
    });
};

export const subscribeToFriends = (userId, callback) => {
    if (!userId) return () => { };
    const q = query(
        collection(db, 'friends'),
        where('users', 'array-contains', userId),
        where('status', '==', 'active')
    );
    return onSnapshot(q, async (snapshot) => {
        const friendships = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        const detailedFriends = await Promise.all(friendships.map(async (friendship) => {
            const friendId = friendship.users.find(uid => uid !== userId);
            try {
                const friendProfile = await getUserProfile(friendId);
                return {
                    ...friendProfile, // id (userId), name, avatar
                    friendshipId: friendship.id, // Needed for removal
                    friendshipCreatedAt: friendship.createdAt // Needed for chat filtering
                };
            } catch (e) {
                console.error("Error resolving friend profile", e);
                return null;
            }
        }));

        // Filter out any failed resolutions
        callback(detailedFriends.filter(f => f !== null));
    });
};

// ==================== REPORTING OPERATIONS ====================

export const reportUser = async (reporterId, reportedUserId, reason) => {
    try {
        const reportsRef = collection(db, 'reports');
        await addDoc(reportsRef, {
            reporterId,
            reportedUserId,
            reason,
            timestamp: serverTimestamp(),
            status: 'pending'
        });
    } catch (error) {
        console.error('Error reporting user:', error);
        throw error;
    }
};

// ==================== ADMIN OPERATIONS ====================

export const getReports = async () => {
    try {
        const reportsRef = collection(db, 'reports');
        // Removed orderBy to avoid needing a composite index
        const q = query(reportsRef, where('status', '==', 'pending'));
        const snapshot = await getDocs(q);

        // Enrich with user data (reporter and reported)
        const reports = await Promise.all(snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            try {
                const [reporter, reported] = await Promise.all([
                    getUserProfile(data.reporterId),
                    getUserProfile(data.reportedUserId)
                ]);
                return {
                    id: docSnap.id,
                    ...data,
                    reporterName: reporter?.name || 'Unknown',
                    reportedName: reported?.name || 'Unknown',
                    reportedAvatar: reported?.avatar,
                    reportedIsAdmin: reported?.isAdmin || false,
                    reportedIsSuperAdmin: reported?.isSuperAdmin || false
                };
            } catch (e) {
                console.warn("Could not resolve users for report", docSnap.id);
                return { id: docSnap.id, ...data };
            }
        }));

        // Client-side sort by timestamp (newest first)
        reports.sort((a, b) => {
            const timeA = a.timestamp?.seconds || 0;
            const timeB = b.timestamp?.seconds || 0;
            return timeB - timeA;
        });

        return reports;
    } catch (error) {
        console.error('Error fetching reports:', error);
        return [];
    }
};

export const resolveReport = async (reportId) => {
    try {
        await updateDoc(doc(db, 'reports', reportId), {
            status: 'resolved',
            resolvedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error resolving report:', error);
        throw error;
    }
};

export const banUser = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        // Safety: Cannot ban super admin
        if (userData?.isSuperAdmin === true) {
            console.warn(`Attempted to ban Super Admin ${userId}. Action blocked.`);
            throw new Error('SECURITY ALERT: You cannot ban the Super Admin.');
        }

        // Safety: Cannot ban admins
        if (userData?.isAdmin === true) {
            throw new Error('Cannot ban an admin. Revoke admin status first.');
        }

        const currentHistory = userData?.banHistory || [];

        await updateDoc(userRef, {
            isBanned: true,
            bannedAt: serverTimestamp(),
            banHistory: [...currentHistory, {
                bannedAt: new Date(),
                action: 'banned'
            }],
            // Revoke Subscriptions
            subscriptionTier: 'global',
            subscriptionStatus: 'none',
            subscriptionEnd: null,
            subscriberEnd: null,
            superSubscriberEnd: null,
            giftNotificationPending: false
        });
    } catch (error) {
        console.error('Error banning user:', error);
        throw error;
    }
};

export const grantAdmin = async (userId) => {
    try {
        await updateDoc(doc(db, 'users', userId), {
            isAdmin: true
        });
        console.log(`User ${userId} is now an admin.`);
    } catch (error) {
        console.error('Error granting admin:', error);
        throw error;
    }
};

export const revokeAdmin = async (userId, requestingUserId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        // Safety: Cannot demote super admin (ABSOLUTE PROTECTION)
        // Even the super admin cannot demote themselves efficiently without transferring it first.
        // This prevents accidental lockout.
        if (userData?.isSuperAdmin === true) {
            throw new Error('CRITICAL: Cannot demote the Super Admin. You must transfer the role to another user first.');
        }

        await updateDoc(userRef, {
            isAdmin: false
        });
        console.log(`User ${userId} is no longer an admin.`);
    } catch (error) {
        console.error('Error revoking admin:', error);
        throw error;
    }
};

export const claimSuperAdmin = async (userId) => {
    try {
        console.log(`Starting Super Admin claim for ${userId}...`);

        // 1. Get all current Super Admins (should be 0 or 1, but we handle N for safety)
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('isSuperAdmin', '==', true));
        const snapshot = await getDocs(q);

        const batch = writeBatch(db);
        let demotedCount = 0;

        // 2. Demote any existing Super Admins
        snapshot.docs.forEach(doc => {
            if (doc.id !== userId) {
                batch.update(doc.ref, {
                    isSuperAdmin: false,
                    isAdmin: true // Fallback to regular admin
                });
                demotedCount++;
            }
        });

        // 3. Promote the target user
        const targetRef = doc(db, 'users', userId);
        batch.update(targetRef, {
            isSuperAdmin: true,
            isAdmin: true, // Implicitly an admin
            isBanned: false // Auto-unban if needed
        }, { merge: true });

        // 4. Commit atomically
        await batch.commit();
        console.log(`✅ Super Admin claimed by ${userId}. Demoted ${demotedCount} others.`);
        return { success: true, demotedCount };

    } catch (error) {
        console.error('Error claiming super admin:', error);
        throw error;
    }
};

export const getBannedUsers = async () => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('isBanned', '==', true));
        const snapshot = await getDocs(q);

        const bannedUsers = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Sort by most recently banned
        bannedUsers.sort((a, b) => {
            const timeA = a.bannedAt?.seconds || 0;
            const timeB = b.bannedAt?.seconds || 0;
            return timeB - timeA;
        });

        return bannedUsers;
    } catch (error) {
        console.error('Error fetching banned users:', error);
        return [];
    }
};

export const submitBanAppeal = async (userId, appealText) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            banAppeal: {
                text: appealText,
                timestamp: new Date()
            }
        });
    } catch (error) {
        console.error('Error submitting ban appeal:', error);
        throw error;
    }
};

export const subscribeToBannedUsers = (callback) => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('isBanned', '==', true));

        return onSnapshot(q, (snapshot) => {
            const bannedIds = snapshot.docs.map(doc => doc.id);
            callback(bannedIds);
        }, (error) => {
            console.error("Error subscribing to banned users:", error);
        });
    } catch (error) {
        console.error("Error setting up banned users subscription:", error);
        throw error;
    }
};

export const subscribeToBannedUsersData = (callback) => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('isBanned', '==', true));

        return onSnapshot(q, (snapshot) => {
            const bannedUsers = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by most recently banned
            bannedUsers.sort((a, b) => {
                const timeA = a.bannedAt?.seconds || 0;
                const timeB = b.bannedAt?.seconds || 0;
                return timeB - timeA;
            });

            callback(bannedUsers);
        }, (error) => {
            console.error("Error subscribing to banned users data:", error);
        });
    } catch (error) {
        console.error("Error setting up banned users data subscription:", error);
        throw error;
    }
};

export const getAllReportsForUser = async (userId) => {
    try {
        const reportsRef = collection(db, 'reports');
        const q = query(reportsRef, where('reportedUserId', '==', userId));
        const snapshot = await getDocs(q);

        const reports = await Promise.all(snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            try {
                const reporter = await getUserProfile(data.reporterId);
                return {
                    id: docSnap.id,
                    ...data,
                    reporterName: reporter?.name || 'Unknown'
                };
            } catch (e) {
                return { id: docSnap.id, ...data, reporterName: 'Unknown' };
            }
        }));

        // Sort by newest first
        reports.sort((a, b) => {
            const timeA = a.timestamp?.seconds || 0;
            const timeB = b.timestamp?.seconds || 0;
            return timeB - timeA;
        });

        return reports;
    } catch (error) {
        console.error('Error fetching user reports:', error);
        return [];
    }
};

export const unbanUser = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const currentHistory = userDoc.data()?.banHistory || [];

        await updateDoc(userRef, {
            isBanned: false,
            bannedAt: null,
            banAppeal: deleteField(), // Reset appeal limit so they can appeal again if re-banned
            banHistory: [...currentHistory, {
                unbannedAt: new Date(),
                action: 'unbanned'
            }]
        });
    } catch (error) {
        console.error('Error unbanning user:', error);
        throw error;
    }
};

// ==================== EMOJI OPERATIONS ====================

/**
 * Get all emojis available to a user based on their subscription tier
 * @param {string} userTier - "free", "subscriber", or "superSubscriber"
 * @returns {Promise<Array>} Array of emoji objects
 */
export const getEmojis = async (userTier = 'global') => {
    try {
        const emojisRef = collection(db, 'emojis');
        // Fetch all active emojis without orderBy to avoid composite index requirement
        const q = query(emojisRef, where('isActive', '==', true));

        const snapshot = await getDocs(q);
        let emojis = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Filter based on tier
        if (userTier === 'subscriber') {
            emojis = emojis.filter(e => e.tier === 'global' || e.tier === 'subscriber');
        } else if (userTier !== 'superSubscriber') {
            // Free/global users get only global emojis
            emojis = emojis.filter(e => e.tier === 'global');
        }

        // Sort client-side by sortOrder
        emojis.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        return emojis;
    } catch (error) {
        console.error('Error fetching emojis:', error);
        return [];
    }
};

/**
 * Subscribe to emoji updates in real-time
 * @param {Function} callback - Called with array of emojis on update
 * @returns {Function} Unsubscribe function
 */
export const subscribeToEmojis = (callback) => {
    const emojisRef = collection(db, 'emojis');
    const q = query(emojisRef, where('isActive', '==', true), orderBy('sortOrder'));

    return onSnapshot(q, (snapshot) => {
        const emojis = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(emojis);
    });
};

/**
 * React to a spark with an emoji
 * @param {string} sparkId - Spark document ID
 * @param {string} emojiId - Emoji ID to react with
 * @param {string} userId - User ID of reactor
 * @returns {Promise<Object>} Updated reaction data
 */
export const reactToSpark = async (sparkId, emojiId, userId) => {
    try {
        const sparkRef = doc(db, 'sparks', sparkId);
        const sparkSnap = await getDoc(sparkRef);

        if (!sparkSnap.exists()) {
            throw new Error('Spark not found');
        }

        const sparkData = sparkSnap.data();
        const reactions = sparkData.reactions || {};

        // Check for any existing reaction from this user BEFORE mutating anything
        const previousEmojiId = Object.keys(reactions).find(key =>
            reactions[key].includes(userId)
        );
        const hasReactedToTarget = previousEmojiId === emojiId;

        let statChange = 0;

        if (hasReactedToTarget) {
            // User is toggling OFF the emoji they already selected
            const updatedReactors = reactions[emojiId].filter(id => id !== userId);
            if (updatedReactors.length === 0) {
                delete reactions[emojiId];
            } else {
                reactions[emojiId] = updatedReactors;
            }
            statChange = -1; // Decrement reaction count
        } else {
            // User is selecting a NEW emoji (or switching)

            // 1. Remove from previous emoji if exists (maintain one-per-user)
            if (previousEmojiId) {
                const updatedPrevious = reactions[previousEmojiId].filter(id => id !== userId);
                if (updatedPrevious.length === 0) {
                    delete reactions[previousEmojiId];
                } else {
                    reactions[previousEmojiId] = updatedPrevious;
                }
                // statChange is 0 because we're just switching types
            } else {
                // This is a brand new reaction
                statChange = 1;
            }

            // 2. Add to new emoji group
            const targetReactors = reactions[emojiId] || [];
            reactions[emojiId] = [...targetReactors, userId];

            // Increment usage count for the emoji
            const emojiRef = doc(db, 'emojis', emojiId);
            await updateDoc(emojiRef, {
                usageCount: increment(1)
            }).catch(err => console.log('Could not update emoji usage:', err));
        }

        // Update spark with new reactions state
        await updateDoc(sparkRef, { reactions });

        // Update author's reactionsReceived stat if there was a net change
        if (statChange !== 0 && sparkData.authorId) {
            const authorRef = doc(db, 'users', sparkData.authorId);
            await updateDoc(authorRef, {
                'stats.reactionsReceived': increment(statChange)
            }).catch(err => console.log('Could not update author stats:', err));
        }

        // NEW: Check Daily Challenge Win Condition (Reacts)
        try {
            // Re-fetch spark or use local math? 
            // We manipulated 'reactions' object locally. Let's use that + existing likes.
            if (sparkData.inspiration) {
                const sysChallengeRef = doc(db, 'system', 'dailyChallenge');
                const sysSnap = await getDoc(sysChallengeRef);

                if (sysSnap.exists()) {
                    const sysData = sysSnap.data();
                    if (sysData.prompt === sparkData.inspiration) {
                        // It matches! Check Qualification and Claim Status
                        // Logic: Always check if they qualify for the queue. Then check claim status.

                        const currentLikes = sparkData.likes || 0;

                        // Calc Totals from our MODIFIED reactions object
                        let totalReacts = 0;
                        if (reactions) {
                            Object.values(reactions).forEach(arr => totalReacts += arr.length);
                        }

                        const totalInteractions = currentLikes + totalReacts;

                        if (totalInteractions >= 3) {
                            console.log("Spark Reached Threshold (3+ interactions). Checking First Spark Status...");

                            // Candidate Object
                            const candidate = {
                                sparkId: sparkId, // FIXED: Use argument sparkId
                                authorId: sparkData.authorId,
                                authorName: sparkData.author || 'A Creator',
                                qualifiedAt: Date.now()
                            };

                            // Get existing queue
                            const currentQueue = sysData.qualifiedSparks || [];
                            const isQueued = currentQueue.some(q => q.sparkId === sparkId);
                            let newQueue = currentQueue;

                            if (!isQueued) {
                                newQueue = [...currentQueue, candidate].sort((a, b) => a.qualifiedAt - b.qualifiedAt);
                            }

                            const claimUpdate = {
                                qualifiedSparks: newQueue
                            };

                            // Check Claim Status
                            if (!sysData.claimedBy) {
                                console.log("...And it is UNCLAIMED! Awarding First Spark Badge.");
                                claimUpdate.claimedBy = sparkData.authorId;
                                claimUpdate.claimedByName = sparkData.author || 'A Creator';
                                claimUpdate.claimedSparkId = sparkId;
                                claimUpdate.claimedAt = serverTimestamp();

                                // Update Spark Badge
                                await updateDoc(sparkRef, { isFirstSpark: true });
                            } else {
                                console.log("...But it is already claimed. Added to succession queue.");
                            }

                            // A. Update System
                            await updateDoc(sysChallengeRef, claimUpdate);

                            // B. Update History
                            const historyRef = collection(db, 'dailyChallengeHistory');
                            const historyQ = query(historyRef, where('prompt', '==', sparkData.inspiration), limit(1));
                            const historySnap = await getDocs(historyQ);
                            if (!historySnap.empty) {
                                await updateDoc(historySnap.docs[0].ref, claimUpdate);
                            }
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Error checking win condition in reactToSpark:", err);
        }

        return {
            emojiId,
            reacted: !hasReactedToTarget,
            count: reactions[emojiId]?.length || 0
        };
    } catch (error) {
        console.error('Error reacting to spark:', error);
        throw error;
    }
};



// ==================== SYSTEM FEEDBACK & BUG REPORTING ====================

export const submitSystemReport = async (reportData, imageFile) => {
    try {
        let imageUrl = null;
        if (imageFile) {
            const storageRef = ref(storage, `system_reports/${Date.now()}_${imageFile.name}`);
            const snapshot = await uploadBytes(storageRef, imageFile);
            imageUrl = await getDownloadURL(snapshot.ref);
        }

        await addDoc(collection(db, 'system_reports'), {
            ...reportData,
            imageUrl,
            status: 'pending',
            timestamp: serverTimestamp()
        });
    } catch (error) {
        console.error('Error submitting system report:', error);
        throw error;
    }
};

export const getSystemReports = async () => {
    try {
        const q = query(collection(db, 'system_reports'), orderBy('timestamp', 'desc'));
        const sn = await getDocs(q);
        return sn.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error('Error fetching system reports:', error);
        throw error;
    }
};

export const resolveSystemReport = async (reportId, newStatus = 'resolved') => {
    try {
        await updateDoc(doc(db, 'system_reports', reportId), {
            status: newStatus
        });
    } catch (error) {
        console.error('Error resolving system report:', error);
        throw error;
    }
};

export const deleteSystemReport = async (reportId) => {
    try {
        await deleteDoc(doc(db, 'system_reports', reportId));
    } catch (error) {
        console.error('Error deleting system report:', error);
        throw error;
    }
};

// Real-time subscription for all reports (Super Admin)
export const subscribeToSystemReports = (callback) => {
    const q = query(
        collection(db, 'system_reports'),
        orderBy('timestamp', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
        const reports = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(reports);
    });
};

// Real-time subscription for pending count (Notification Badge)
export const subscribeToPendingReportCount = (callback) => {
    const q = query(
        collection(db, 'system_reports'),
        where('status', '==', 'pending')
    );
    return onSnapshot(q, (snapshot) => {
        callback(snapshot.size);
    });
};

// System Configuration
export const subscribeToSystemConfig = (callback) => {
    return onSnapshot(doc(db, 'system', 'config'), (doc) => {
        // Default to enabled if config doesn't exist
        callback(doc.exists() ? doc.data() : { enableReporting: true });
    });
};

export const updateSystemConfig = async (updates) => {
    try {
        await setDoc(doc(db, 'system', 'config'), updates, { merge: true });
    } catch (error) {
        console.error("Error updating system config:", error);
        throw error;
    }
};

// ==================== CUSTOM EMOTES OPERATIONS ====================

export const subscribeToEmotes = (callback) => {
    try {
        // Query by createdAt to ensure we get ALL documents (even those without 'order')
        // We will sort by 'order' client-side to handle the mixed state.
        // Query all emotes (client-side sort handles order)
        const q = query(collection(db, 'emotes'));
        return onSnapshot(q, (snapshot) => {
            const emotes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Client-side Sort: Order (asc) -> CreatedAt (desc) fallback
            emotes.sort((a, b) => {
                const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER; // Put undefined orders at the end
                const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;

                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                // Fallback to newest first if orders are equal (or both undefined)
                return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
            });

            callback(emotes);
        });
    } catch (error) {
        console.error("Error subscribing to emotes:", error);
        return () => { };
    }
};

export const addEmote = async (code, name, url, tier) => {
    try {
        // Get generic max order to valid collision
        // Ideally we'd query max order for the tier, but for now simple append is fine.
        // We will default to a high order or timestamp-based info if needed, 
        // but 9999 ensures it goes to end if others are 0,1,2. 
        // Better: let's use Date.now() for order if we don't have a better way, 
        // to ensure it's at the end of the list initially.
        await addDoc(collection(db, 'emotes'), {
            code, // e.g. :musePog:
            name,
            url,
            tier: tier || 'global',
            order: Date.now(), // Default to end of list
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error("Error adding emote:", error);
        throw error;
    }
};

export const reorderEmotes = async (updates) => {
    try {
        const batch = writeBatch(db);
        updates.forEach(({ id, order, tier }) => {
            const ref = doc(db, 'emotes', id);
            batch.update(ref, { order, tier });
        });
        await batch.commit();
    } catch (error) {
        console.error("Error reordering emotes:", error);
        throw error;
    }
};

export const deleteEmote = async (emoteId) => {
    try {
        await deleteDoc(doc(db, 'emotes', emoteId));
    } catch (error) {
        console.error("Error deleting emote:", error);
        throw error;
    }
};

export const getEmoteByCode = async (code) => {
    try {
        const q = query(collection(db, 'emotes'), where('code', '==', code));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            return snapshot.docs[0].data();
        }
        return null;
    } catch (error) {
        console.error("Error getting emote:", error);
        return null;
    }
};

// ==================== THEME PACK OPERATIONS ====================

export const createTheme = async (name, initialOverrides = {}) => {
    try {
        await addDoc(collection(db, 'themes'), {
            name,
            overrides: initialOverrides, // Map of emoteId -> url
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error("Error creating theme:", error);
        throw error;
    }
};

export const updateThemeOverride = async (themeId, emoteId, url) => {
    try {
        const themeRef = doc(db, 'themes', themeId);
        // We use dot notation to update a specific key in the map
        await updateDoc(themeRef, {
            [`overrides.${emoteId}`]: url
        });
    } catch (error) {
        console.error("Error updating theme override:", error);
        throw error;
    }
};

export const removeThemeOverride = async (themeId, emoteId) => {
    try {
        const themeRef = doc(db, 'themes', themeId);
        await updateDoc(themeRef, {
            [`overrides.${emoteId}`]: deleteField()
        });
    } catch (error) {
        console.error("Error removing override:", error);
        throw error;
    }
};

export const deleteTheme = async (themeId) => {
    try {
        await deleteDoc(doc(db, 'themes', themeId));
    } catch (error) {
        console.error("Error deleting theme:", error);
        throw error;
    }
};

export const subscribeToThemes = (callback) => {
    const q = query(collection(db, 'themes'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
        const themes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(themes);
    });
};

export const setActiveTheme = async (themeId) => {
    try {
        // We store the active theme ID in system/config
        // If themeId is null, it means "Default" (no overrides)
        await setDoc(doc(db, 'system', 'config'), {
            activeThemeId: themeId
        }, { merge: true });
    } catch (error) {
        console.error("Error setting active theme:", error);
        throw error;
    }
};
// ==================== THEME STORAGE HELPERS ====================
export const checkThemeStorageExists = async (themeId) => {
    try {
        const folderRef = ref(storage, `emotes/images/${themeId}`);
        const listRes = await listAll(folderRef);
        // If there are files (items) or subfolders (prefixes), we consider it "existing"
        return listRes.items.length > 0 || listRes.prefixes.length > 0;
    } catch (error) {
        console.error("Error checking theme storage:", error);
        return false; // Assume safe if error? Or fail safe? Usually empty behaves fine.
    }
};

export const createThemeFolder = async (themeId) => {
    try {
        // Create a placeholder file to "initialize" the folder
        const fileRef = ref(storage, `emotes/images/${themeId}/_created_by_admin.txt`);
        await uploadString(fileRef, `Theme Folder: ${themeId}\nCreated at: ${new Date().toISOString()}`);
        return true;
    } catch (error) {
        console.error("Error creating theme folder:", error);
        throw error;
    }
};

export const restoreThemeFromStorage = async (themeId, allEmotes) => {
    try {
        const folderRef = ref(storage, `emotes/images/${themeId}`);
        const listRes = await listAll(folderRef);
        const overrides = {};

        // Helper to strip extension and colons: "smile.png" -> "smile", ":smile:" -> "smile"
        const cleanName = (str) => str.split('.')[0].replace(/:/g, '').toLowerCase();

        for (const itemRef of listRes.items) {
            const fileName = itemRef.name; // e.g. "smile.png" or "_created_by..."
            if (fileName.startsWith('_')) continue; // Skip system files

            const targetName = cleanName(fileName);

            // Find matching emote
            const match = allEmotes.find(e => cleanName(e.code) === targetName);

            if (match) {
                const url = await getDownloadURL(itemRef);
                overrides[match.id] = url;
            }
        }
        return overrides;
    } catch (error) {
        console.error("Error restoring theme:", error);
        return {};
    }
};

export const checkFileExists = async (path) => {
    try {
        const fileRef = ref(storage, path);
        await getDownloadURL(fileRef);
        return true; // Exists
    } catch (error) {
        if (error.code === 'storage/object-not-found') return false;
        throw error;
    }
};

export const uploadEmoteImage = async (file, folder, filename) => {
    try {
        const fileRef = ref(storage, `${folder}/${filename}`);
        await uploadBytes(fileRef, file);
        return await getDownloadURL(fileRef);
    } catch (error) {
        console.error("Error uploading emote:", error);
        throw error;
    }
};

export const getFileUrl = async (path) => {
    try {
        const fileRef = ref(storage, path);
        const url = await getDownloadURL(fileRef);
        return url;
    } catch (error) {
        return null; // Not found
    }
};

// Tutorial Tracking
export const markTutorialSeen = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { hasSeenTutorial: true });
    } catch (error) {
        console.error("Error marking tutorial seen:", error);
    }
};

export const resetTutorialSeen = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { hasSeenTutorial: false });
    } catch (error) {
        console.error("Error resetting tutorial:", error);
    }
};

// ==================== GIFTING OPERATIONS ====================

// Verify payment and generate code (Client-side implementation of "Serverless" logic)
export const validatePaymentAndCreateGiftCode = async (userId, sessionId) => {
    try {
        console.log("Verifying payment for session:", sessionId);

        // MVP: Limit to 10 retries (10 seconds)
        let foundPayment = false;
        const paymentsRef = collection(db, 'customers', userId, 'payments');

        for (let i = 0; i < 10; i++) {
            const q = query(
                paymentsRef,
                where('status', '==', 'succeeded'),
                orderBy('created', 'desc'),
                limit(5)
            );
            const snapshot = await getDocs(q);

            // Look for a payment that looks like a gift
            // And ideally matches the time or is unused
            const match = snapshot.docs.find(doc => {
                const data = doc.data();
                // We trust the latest succeeded gift payment is the one we want?
                // Ideally we match PaymentIntent ID but we don't have it easily.
                return data.metadata?.isGift === 'true';
            });

            if (match) {
                foundPayment = match; // This is the payment doc
                break;
            }

            // Wait 1s
            await new Promise(r => setTimeout(r, 1000));
        }

        if (!foundPayment) {
            throw new Error("Payment confirmation not found. Please contact support if you were charged.");
        }

        // 2. Check if a code already exists for this payment (Idempotency)
        const paymentId = foundPayment.id;
        const codesRef = collection(db, 'gift_codes');
        // SECURITY FIX: Must filter by 'purchasedBy' to satisfy Firestore Rules allow list check
        const existingQ = query(
            codesRef,
            where('paymentId', '==', paymentId),
            where('purchasedBy', '==', userId)
        );
        const existingSnap = await getDocs(existingQ);

        if (!existingSnap.empty) {
            console.log("Code already generated for this payment.");
            return existingSnap.docs[0].data(); // Return existing
        }

        // 3. Generate New Code
        const tier = foundPayment.data().metadata.giftTier || 'super_fan'; // Fallback
        const code = generateGiftCode();
        const giftDoc = {
            code,
            tier, // 'supporter' or 'super_fan'
            durationDays: 30,
            status: 'active',
            purchasedBy: userId,
            paymentId: paymentId,
            createdAt: serverTimestamp(),
            redeemedBy: null,
            redeemedAt: null
        };

        // KEY CHANGE: Use the Code as the Document ID to enable 'get(doc)' security rules
        await setDoc(doc(db, 'gift_codes', code), giftDoc);
        return giftDoc;

    } catch (error) {
        console.error("Error generating gift code:", error);
        throw error;
    }
};

const generateGiftCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No I, O, 0, 1
    // Format: GIFT-XXXX-YYYY-ZZZZ (12 random chars = 32^12 combinations = ~1.1 quintillion)
    let code = 'GIFT-';
    for (let i = 0; i < 4; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    code += '-';
    for (let i = 0; i < 4; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    code += '-';
    for (let i = 0; i < 4; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    return code;
};

// 2. Process a Direct Gift Payment (Found by Session ID)
export const processDirectGiftPayment = async (userId, sessionId) => {
    try {
        console.log("Processing direct gift for session:", sessionId);

        // A. Verify Payment (Reuse similar logic to validatePaymentAndCreateGiftCode but flexible)
        let foundPayment = false;
        const paymentsRef = collection(db, 'customers', userId, 'payments');

        // We can't query by session_id directly on payments usually unless metadata copied it.
        // But the Extension usually puts the stripe metadata on the payment doc.
        // We'll search for recent successful payments with isGift=true

        for (let i = 0; i < 15; i++) { // 15s timeout
            const q = query(
                paymentsRef,
                where('status', '==', 'succeeded'),
                orderBy('created', 'desc'),
                limit(5)
            );
            const snapshot = await getDocs(q);

            // Heuristic match: Look for metadata that matches our constraints?
            // Ideally we'd match exact Metadata recipients if possible.
            const match = snapshot.docs.find(doc => {
                const data = doc.data();
                // We assume the LATEST gift payment is the one (race condition risk low for single user)
                return data.metadata?.isGift === 'true';
            });

            if (match) {
                foundPayment = match; // This is the payment doc
                break;
            }
            await new Promise(r => setTimeout(r, 1000));
        }

        if (!foundPayment) {
            throw new Error("Payment confirmation not found. Please contact support.");
        }

        const paymentData = foundPayment.data();
        const paymentId = foundPayment.id;
        const metadata = paymentData.metadata || {};

        const recipientId = metadata.recipientId;
        const recipientName = metadata.recipientName;
        const tier = metadata.giftTier || 'supporter';

        if (!recipientId) {
            console.log("No recipient specified - treating as Open Gift Code purchase.");
            return await validatePaymentAndCreateGiftCode(userId, sessionId);
        }

        // B. Check for existing code for this payment (Idempotency)
        // B. Check for existing code for this payment (Idempotency)
        // Must filter by 'giftedBy' to satisfy Firestore 'allow list' security rules
        const codesRef = collection(db, 'gift_codes');
        const existingQ = query(
            codesRef,
            where('paymentId', '==', paymentId),
            where('giftedBy', '==', userId)
        );
        const existingSnap = await getDocs(existingQ);

        if (!existingSnap.empty) {
            console.log("Gift code already exists for payment.");
            return existingSnap.docs[0].data();
        }

        // C. Generate and Send Code
        const code = await sendDirectGiftCode(userId, "You (Direct Gift)", recipientId, tier); // Use helper, but we need to inject paymentId

        // D. Update the code doc with paymentId for link/idempotency
        // sendDirectGiftCode doesn't take paymentId, so we update it.
        await updateDoc(doc(db, 'gift_codes', code), {
            paymentId: paymentId,
            purchasedBy: recipientId, // Ownership is recipient
            giftedBy: userId,         // Payer is gifter
            giftedByName: recipientName ? `Gifter` : "A Friend" // Logic handled in sendDirectGiftCode actually takes gifterName, let's patch that call.
        });

        // ACTUALLY: sendDirectGiftCode takes (gifterId, gifterName, recipientId, tier). 
        // We should pass the User's name if we have it, but here we only have ID?
        // Let's assume the caller passes the user's name or we fetch profile. 
        // For efficiency, we'll update the doc with payment info.

        return { code, recipientId, tier, recipientName };

    } catch (error) {
        console.error("Error processing direct gift:", error);
        throw error;
    }
};

export const sendDirectGiftCode = async (gifterId, gifterName, recipientId, tier) => {
    try {
        const code = generateGiftCode();
        const giftCodesRef = doc(db, 'gift_codes', code);

        await setDoc(giftCodesRef, {
            code,
            tier,
            status: 'active',
            createdAt: serverTimestamp(),
            purchasedBy: recipientId, // Ownership transferred to recipient
            giftedBy: gifterId,
            giftedByName: gifterName,
            isDirectGift: true
        });

        return code;
    } catch (error) {
        console.error("Error sending direct gift code:", error);
        throw error;
    }
};

export const getUserGiftCodes = async (userId) => {
    try {
        const q = query(collection(db, 'gift_codes'), where('purchasedBy', '==', userId), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error getting gift codes:", error);
        return [];
    }
};

export const subscribeToUserGiftCodes = (userId, callback) => {
    const q = query(collection(db, 'gift_codes'), where('purchasedBy', '==', userId), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
        const codes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(codes);
    });
};

export const redeemGiftCode = async (userId, code) => {
    try {
        const normalizedCode = code.trim().toUpperCase();

        // 1. Get the code directly by ID (Security Rule: allow get if true)
        const giftRef = doc(db, 'gift_codes', normalizedCode);
        const giftSnap = await getDoc(giftRef);

        if (!giftSnap.exists()) {
            throw new Error("Invalid code.");
        }

        const giftData = giftSnap.data();

        if (giftData.status !== 'active') {
            throw new Error("Code already redeemed.");
        }

        /* 
           tier mapping: 
           'supporter' -> 'subscriber'
           'super_fan' -> 'super_subscriber'
        */
        const targetTier = giftData.tier === 'super_fan' ? 'super_subscriber' : 'subscriber';

        // 1. Mark Code Redeemed
        await updateDoc(giftRef, {
            status: 'redeemed',
            redeemedBy: userId,
            redeemedAt: serverTimestamp()
        });

        // 2. Add Days
        await addSubscriptionDays(userId, targetTier, giftData.durationDays || 30);

        return { success: true, tier: targetTier, days: giftData.durationDays };

    } catch (error) {
        console.error("Error redeeming code:", error);
        throw error;
    }
};

export const deleteStripeCustomerId = async (uid) => {
    try {
        await updateDoc(doc(db, 'customers', uid), {
            stripeId: deleteField(),
            stripeLink: deleteField()
        });
        console.log(`Deleted invalid Stripe Customer ID for ${uid}`);
    } catch (error) {
        console.error("Error deleting Stripe Customer ID:", error);
    }
};
